import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { FilterConditionDTO, OffsetPageQueryDTO } from "@/shared/contracts/base";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { confirm, toast } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useCancelClassSession, useClassSessionsQuery } from "../api/queries";
import { BulkUpdateClassSessionInstructorsDialog } from "../ui/BulkUpdateClassSessionInstructorsDialog";
import { BulkUpdateClassSessionTemplateDialog } from "../ui/BulkUpdateClassSessionTemplateDialog";
import {
  ClassSessionFilter,
  EMPTY_FILTERS,
  useClassSessionFilterSummary,
} from "../ui/ClassSessionFilter";
import { ClassSessionTable } from "../ui/ClassSessionTable";
import { ClassSessionToolbar } from "../ui/ClassSessionToolbar";

export function ClassSessionsPage() {
  const nav = useNavigate({ from: "/app/classes/sessions" });
  const search: OffsetPageQueryDTO = useSearch({
    from: "/app/classes/sessions",
  });
  const [bulkUpdateOpen, setBulkUpdateOpen] = useState(false);
  const [bulkInstructorsOpen, setBulkInstructorsOpen] = useState(false);

  const query = useMemo<OffsetPageQueryDTO>(
    () => ({
      limit: search.limit ?? 20,
      offset: search.offset ?? 0,
      sort: search.sort,
      filters: search.filters,
    }),
    [search.limit, search.offset, search.sort, search.filters],
  );

  const filters = useMemo(
    () => search.filters as FilterConditionDTO[] | undefined,
    [search.filters],
  );
  const { hasFilter } = useClassSessionFilterSummary(filters);

  const list = useClassSessionsQuery(query);
  const canCreate = useActionPermission(["classes:create"]);
  const canUpdate = useActionPermission(["classes:update"]);
  const cancelSession = useCancelClassSession();

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ຈັດຕາຕະລາງຄລາດສ໌" },
          ]}
        />
      </Header>

      <Main>
        <ClassSessionToolbar
          canManage={!!canCreate}
          canUpdate={!!canUpdate}
          bulkUpdateEnabled={hasFilter}
          onCreate={() => nav({ to: "/app/classes/sessions/create" })}
          onBulkCreate={
            canCreate
              ? () => nav({ to: "/app/classes/sessions/bulk-create" })
              : undefined
          }
          onBulkUpdateTemplate={
            canUpdate ? () => setBulkUpdateOpen(true) : undefined
          }
          onBulkUpdateInstructors={
            canUpdate ? () => setBulkInstructorsOpen(true) : undefined
          }
        />

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <ClassSessionFilter />

          <ClassSessionTable
            data={list.data?.data ?? []}
            isLoading={list.isLoading}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={list.data?.meta?.total ?? 0}
            onPaginationChange={(offset, limit) =>
              nav({ search: { ...search, offset, limit } })
            }
            sortBy={search.sort ? search.sort[0]?.field : undefined}
            sortOrder={
              search.sort ? (search.sort[0]?.dir as "asc" | "desc") : undefined
            }
            onSortingChange={(id, desc) =>
              nav({
                search: {
                  ...search,
                  sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                },
              })
            }
            onView={(session) => {
              nav({
                to: "/app/classes/sessions/$id",
                params: { id: session.id },
              });
            }}
            onEdit={(session) => {
              nav({
                to: "/app/classes/sessions/$id/edit",
                params: { id: session.id },
              });
            }}
            onMove={(session) => {
              nav({
                to: "/app/classes/sessions/$id/edit",
                params: { id: session.id },
              });
            }}
            onCancel={async (id) => {
              const ok = await confirm({
                title: "ຍົກເລີກຊົ່ວໂມງຮຽນ",
                description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະຍົກເລີກ ຊົ່ວໂມງຮຽນ ນີ້?",
                actionText: "ຍົກເລີກ",
                ActionProps: {
                  variant: "destructive",
                },
              });
              if (ok) {
                await toast.promise(cancelSession.run(id, "ຍົກເລີກໂດຍ admin"), {
                  loading: "ກຳລັງຍົກເລີກ...",
                  success: "ຍົກເລີກ ຊົ່ວໂມງຮຽນ ສຳເລັດ",
                  error: "ຍົກເລີກ ຊົ່ວໂມງຮຽນ ລົ້ມເຫຼວ",
                });
                list.refetch();
              }
            }}
          />
        </div>

        <BulkUpdateClassSessionTemplateDialog
          open={bulkUpdateOpen}
          onOpenChange={setBulkUpdateOpen}
          filters={filters ?? EMPTY_FILTERS}
        />

        <BulkUpdateClassSessionInstructorsDialog
          open={bulkInstructorsOpen}
          onOpenChange={setBulkInstructorsOpen}
          filters={filters ?? EMPTY_FILTERS}
        />
      </Main>
    </>
  );
}
